<template>
  <div>
    <v-alert
      v-if="errorMessage"
      type="error"
      text
      dismissible
      class="mx-5 mt-4 mb-0"
    >
      {{ errorMessage }}
    </v-alert>

    <v-card flat class="rmr-table-shell social-reporting-table">
      <rmr-table-header>
        <template #heading>
          <div class="rmr-table-shell__eyebrow">Données sociales</div>
          <div class="rmr-table-shell__title">{{ config.title }}</div>
          <div
            v-if="resource === 'posts'"
            class="social-reporting-table__basis"
          >
            {{ basisDescription }}
          </div>
        </template>
      </rmr-table-header>

      <social-reporting-filters
        v-model="filters"
        :options="filterOptions"
        :loading="loading"
        :show-dates="resource !== 'accounts'"
        :show-source="false"
        :post-filters="resource === 'posts'"
        :dates-required="resource === 'posts'"
        @apply="applyFilters"
        @reset="resetFilters"
      >
        <template #actions>
          <rmr-action-button
            :label="`Colonnes (${selectedColumns.length})`"
            :icon="icons.mdiTableColumn"
            variant="primary"
            size="sm"
            :disabled="loading"
            @click="columnsDialog = true"
          />
          <rmr-action-button
            label="Exporter Excel"
            :icon="icons.mdiMicrosoftExcel"
            variant="primary"
            size="sm"
            :color="RMR_GREEN"
            :loading="exporting"
            :disabled="loading || !selectedColumns.length"
            @click="exportExcel"
          />
        </template>
      </social-reporting-filters>

      <v-card-text class="rmr-table-shell__body">
        <v-data-table
          :headers="visibleHeaders"
          :items="normalizedRows"
          :options.sync="tableOptions"
          :server-items-length="total"
          :loading="loading"
          :footer-props="{ 'items-per-page-options': [25, 50, 100, 200] }"
          :items-per-page="tableOptions.itemsPerPage"
          locale="fr-FR"
          class="rmr-data-table social-reporting-data-table"
          must-sort
        >
          <template v-slot:loading>
            <div class="rmr-data-table__state">
              <v-progress-circular
                indeterminate
                :color="RMR_INDIGO"
                size="28"
                class="mb-2"
              />
              <div class="rmr-data-table__state-title">
                Chargement des données sociales
              </div>
            </div>
          </template>

          <template
            v-for="column in visibleColumns"
            v-slot:[`item.${column.value}`]="{ item }"
          >
            <social-reporting-cell
              :key="column.value"
              :column="column"
              :item="item"
            />
          </template>

          <template v-slot:no-data>
            <div class="rmr-data-table__state">
              <v-icon :color="RMR_TEXT_SUBTLE" size="32" class="mb-2">
                {{ icons.mdiDatabaseOffOutline }}
              </v-icon>
              <div class="rmr-data-table__state-title">Aucune donnée</div>
              <div class="rmr-data-table__state-text">
                Modifiez les filtres ou vérifiez que la collecte a été exécutée
                sur cette période.
              </div>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <rmr-modal
      v-model="columnsDialog"
      variant="form"
      eyebrow="Affichage"
      title="Colonnes affichées"
      subtitle="Choisissez les informations visibles dans le tableau et dans l’export Excel."
      :icon="icons.mdiTableColumn"
      :max-width="640"
    >
      <template #body>
        <v-card-text class="pa-6">
          <rmr-autocomplete
            v-model="selectedColumns"
            :items="columnOptions"
            item-text="displayName"
            item-value="value"
            label="Sélectionner les colonnes"
            multiple
            chips
            small-chips
            deletable-chips
            clearable
          />
        </v-card-text>
      </template>
      <template #footer>
        <v-card-actions class="pa-4">
          <rmr-action-button
            label="Par défaut"
            variant="transparent"
            :color="RMR_INDIGO"
            size="sm"
            @click="resetColumns"
          />
          <v-spacer />
          <rmr-action-button
            label="Fermer"
            variant="primary"
            size="sm"
            @click="columnsDialog = false"
          />
        </v-card-actions>
      </template>
    </rmr-modal>
  </div>
</template>

<script>
import isEqual from "lodash/isEqual";
import {
  mdiDatabaseOffOutline,
  mdiMicrosoftExcel,
  mdiTableColumn,
} from "@mdi/js";

import useAxios from "@/hooks/useAxios";
import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrAutocomplete from "@/components/ui/forms/RmrAutocomplete.vue";
import RmrModal from "@/components/ui/layout/RmrModal.vue";
import RmrTableHeader from "@/components/ui/layout/RmrTableHeader.vue";
import { RMR_GREEN, RMR_INDIGO, RMR_TEXT_SUBTLE } from "@/styles/tokens";
import SocialReportingCell from "./SocialReportingCell.vue";
import SocialReportingFilters, {
  emptyFilters,
} from "./SocialReportingFilters.vue";
import {
  readQueryList,
  readQueryString,
  replaceQueryParams,
} from "./queryState";
import {
  COMMON_EXPORT_LABELS,
  EXCEL_NUMERIC_FIELDS,
  POST_BASIS_OPTIONS,
  RESOURCE_CONFIG,
} from "./config";

const POST_BASIS_VALUES = POST_BASIS_OPTIONS.map((option) => option.value);

const FILTERS_ENDPOINT = "/audience/social-reporting/filters";
const RESOURCE_ENDPOINT = "/audience/social-reporting";
const COMMON_FILTER_QUERY_KEYS = [
  "brand_ids",
  "networks",
  "account_ids",
  "site_ids",
];
const POST_FILTER_QUERY_KEYS = [
  "date_from",
  "date_to",
  "basis",
  "authors",
  "post_types",
  "statuses",
  "search",
];

const valueOrUndefined = (values) =>
  values && values.length ? values.join(",") : undefined;

const localIsoDate = (value) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const defaultFilters = (resource) => {
  const filters = emptyFilters();
  if (resource !== "posts") return filters;

  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 6);
  filters.dateFrom = localIsoDate(start);
  filters.dateTo = localIsoDate(end);
  return filters;
};

const filtersFromQuery = (resource, query) => {
  const filters = defaultFilters(resource);
  const dateFrom = readQueryString(query.date_from);
  const dateTo = readQueryString(query.date_to);
  if (resource === "posts" && dateFrom && dateTo) {
    filters.dateFrom = dateFrom;
    filters.dateTo = dateTo;
  }
  filters.brandIds = readQueryList(query.brand_ids);
  filters.networks = readQueryList(query.networks);
  filters.accountIds = readQueryList(query.account_ids, { integer: true });
  filters.siteIds = readQueryList(query.site_ids, { integer: true });
  if (resource === "posts") {
    filters.authors = readQueryList(query.authors);
    filters.postTypes = readQueryList(query.post_types);
    filters.search = readQueryString(query.search);
    const basis = readQueryString(query.basis, "published");
    filters.basis = POST_BASIS_VALUES.includes(basis) ? basis : "published";
  }
  return filters;
};

const downloadBlob = (blob, filename) => {
  const link = document.createElement("a");
  const objectUrl = URL.createObjectURL(blob);
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
};

export default {
  name: "SocialReportingResource",
  components: {
    RmrActionButton,
    RmrAutocomplete,
    RmrModal,
    RmrTableHeader,
    SocialReportingCell,
    SocialReportingFilters,
  },
  props: {
    resource: {
      type: String,
      required: true,
      validator: (value) => Object.keys(RESOURCE_CONFIG).includes(value),
    },
  },
  setup() {
    const { axiosGet } = useAxios();
    return {
      axiosGet,
      icons: {
        mdiDatabaseOffOutline,
        mdiMicrosoftExcel,
        mdiTableColumn,
      },
      RMR_GREEN,
      RMR_INDIGO,
      RMR_TEXT_SUBTLE,
    };
  },
  data() {
    const config = RESOURCE_CONFIG[this.resource];
    return {
      filters: filtersFromQuery(this.resource, this.$route.query),
      filterOptions: {
        sources: [],
        brands: [],
        networks: [],
        accounts: [],
        sites: [],
        authors: [],
        postTypes: [],
      },
      rows: [],
      total: 0,
      loading: false,
      exporting: false,
      errorMessage: "",
      requestSequence: 0,
      activeRequestSignature: "",
      lastCompletedRequestSignature: "",
      columnsDialog: false,
      selectedColumns: [...config.defaultColumns],
      tableOptions: {
        page: 1,
        itemsPerPage: 50,
        sortBy: [config.defaultSort],
        sortDesc: [config.defaultSortDesc],
      },
    };
  },
  computed: {
    config() {
      return RESOURCE_CONFIG[this.resource];
    },
    basisDescription() {
      const option = POST_BASIS_OPTIONS.find(
        (item) => item.value === this.filters.basis
      );
      return option ? option.description : "";
    },
    columnOptions() {
      return this.config.columns.map((column) => ({
        ...column,
        displayName: `${column.group} · ${column.text}`,
      }));
    },
    visibleColumns() {
      const selected = new Set(this.selectedColumns);
      return this.config.columns.filter((column) => selected.has(column.value));
    },
    visibleHeaders() {
      return this.visibleColumns.map((column) => ({
        text: column.text,
        value: column.value,
        align: column.align || "start",
        sortable: column.sortable !== false,
      }));
    },
    normalizedRows() {
      return this.rows.map((row) => {
        const normalized = { ...row };
        this.visibleColumns.forEach((column) => {
          if (column.source) normalized[column.value] = row[column.source];
        });
        return normalized;
      });
    },
  },
  watch: {
    tableOptions: {
      deep: true,
      handler() {
        this.fetchRows();
      },
    },
    "$route.query": {
      deep: true,
      handler(query) {
        // Both table instances are cached by the parent. Only the visible one
        // must react to URL navigation, otherwise the hidden table would also
        // launch API requests.
        const activeResource = readQueryString(query.view, "posts");
        if (activeResource !== this.resource) return;
        const restoredFilters = filtersFromQuery(this.resource, query);
        if (isEqual(restoredFilters, this.filters)) return;
        this.filters = restoredFilters;
        this.fetchFilterOptions();
        if (this.tableOptions.page !== 1) this.tableOptions.page = 1;
        else this.fetchRows(true);
      },
    },
  },
  created() {
    this.fetchFilterOptions();
    this.fetchRows();
  },
  methods: {
    buildParams(includePagination = true) {
      const params = {
        brand_ids: valueOrUndefined(this.filters.brandIds),
        networks: valueOrUndefined(this.filters.networks),
        account_ids: valueOrUndefined(this.filters.accountIds),
        site_ids: valueOrUndefined(this.filters.siteIds),
        sources: valueOrUndefined(this.filters.sources),
        sort_by: this.tableOptions.sortBy[0] || this.config.defaultSort,
        sort_desc:
          this.tableOptions.sortDesc.length > 0
            ? this.tableOptions.sortDesc[0]
            : this.config.defaultSortDesc,
      };

      if (this.resource !== "accounts") {
        params.date_from = this.filters.dateFrom || undefined;
        params.date_to = this.filters.dateTo || undefined;
      }
      if (this.resource === "posts") {
        params.authors = valueOrUndefined(this.filters.authors);
        params.post_types = valueOrUndefined(this.filters.postTypes);
        params.search = this.filters.search || undefined;
        params.basis = this.filters.basis || "published";
      }
      if (includePagination) {
        params.page = this.tableOptions.page;
        params.page_size = this.tableOptions.itemsPerPage;
      }
      return params;
    },
    async fetchFilterOptions() {
      try {
        const response = await this.axiosGet(FILTERS_ENDPOINT, {
          resource: this.resource,
          date_from:
            this.resource === "posts" ? this.filters.dateFrom : undefined,
          date_to: this.resource === "posts" ? this.filters.dateTo : undefined,
        });
        const data = response.data || {};
        this.filterOptions = {
          sources: data.sources || [],
          brands: (data.brands || []).map((brand) => ({
            ...brand,
            displayName: brand.name || brand.id,
          })),
          networks: data.networks || [],
          accounts: data.accounts || [],
          sites: (data.sites || []).map((site) => ({
            ...site,
            displayName: site.domain
              ? `${site.name || site.domain} · ${site.domain}`
              : site.name,
          })),
          authors: data.authors || [],
          postTypes: data.post_types || [],
        };
      } catch (error) {
        this.errorMessage = "Impossible de charger les valeurs de filtres.";
      }
    },
    async fetchRows(force = false) {
      const params = this.buildParams();
      const requestSignature = JSON.stringify(params);
      if (
        !force &&
        (requestSignature === this.activeRequestSignature ||
          requestSignature === this.lastCompletedRequestSignature)
      ) {
        return;
      }

      // Plusieurs changements rapides (page, tri, filtres) peuvent se
      // chevaucher. Seule la réponse de la requête la plus récente est rendue.
      const requestId = ++this.requestSequence;
      this.activeRequestSignature = requestSignature;
      this.loading = true;
      this.errorMessage = "";
      try {
        const response = await this.axiosGet(
          `${RESOURCE_ENDPOINT}/${this.config.endpoint}`,
          params
        );
        if (requestId !== this.requestSequence) return;
        this.rows = response.data.rows || [];
        this.total = response.data.total || 0;
        this.lastCompletedRequestSignature = requestSignature;
      } catch (error) {
        if (requestId !== this.requestSequence) return;
        this.rows = [];
        this.total = 0;
        this.errorMessage =
          "Impossible de charger les données du reporting social.";
      } finally {
        if (requestId === this.requestSequence) {
          this.loading = false;
          this.activeRequestSignature = "";
        }
      }
    },
    filterQueryKeys() {
      return this.resource === "posts"
        ? [...COMMON_FILTER_QUERY_KEYS, ...POST_FILTER_QUERY_KEYS]
        : COMMON_FILTER_QUERY_KEYS;
    },
    syncFiltersToUrl() {
      return replaceQueryParams(
        this.$router,
        this.$route,
        this.filterQueryKeys(),
        {
          brand_ids: this.filters.brandIds,
          networks: this.filters.networks,
          account_ids: this.filters.accountIds,
          site_ids: this.filters.siteIds,
          date_from:
            this.resource === "posts" ? this.filters.dateFrom : undefined,
          date_to: this.resource === "posts" ? this.filters.dateTo : undefined,
          authors: this.resource === "posts" ? this.filters.authors : undefined,
          post_types:
            this.resource === "posts" ? this.filters.postTypes : undefined,
          search: this.resource === "posts" ? this.filters.search : undefined,
          // "published" is the default: kept out of the URL to leave old
          // links untouched.
          basis:
            this.resource === "posts" && this.filters.basis !== "published"
              ? this.filters.basis
              : undefined,
        }
      );
    },
    async applyFilters() {
      await this.syncFiltersToUrl();
      this.fetchFilterOptions();
      if (this.tableOptions.page !== 1) this.tableOptions.page = 1;
      else this.fetchRows(true);
    },
    async resetFilters() {
      this.filters = defaultFilters(this.resource);
      await this.syncFiltersToUrl();
      this.fetchFilterOptions();
      if (this.tableOptions.page !== 1) this.tableOptions.page = 1;
      else this.fetchRows(true);
    },
    resetColumns() {
      this.selectedColumns = [...this.config.defaultColumns];
    },
    async exportExcel() {
      this.exporting = true;
      this.errorMessage = "";
      try {
        const response = await this.axiosGet(
          `${RESOURCE_ENDPOINT}/${this.config.endpoint}/export`,
          {
            ...this.buildParams(false),
            fields: this.visibleColumns
              .map((column) => column.source || column.value)
              .join(","),
          },
          { responseType: "arraybuffer" }
        );
        const XLSX = await import("xlsx");
        const workbook = XLSX.read(response.data, { type: "array", raw: true });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        this.prepareExcelWorksheet(XLSX, worksheet);
        worksheet["!autofilter"] = { ref: worksheet["!ref"] || "A1" };
        workbook.SheetNames[0] = this.config.title.slice(0, 31);
        workbook.Sheets[workbook.SheetNames[0]] = worksheet;
        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });
        downloadBlob(
          new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
          `reporting-social-${this.resource}-${new Date()
            .toISOString()
            .slice(0, 10)}.xlsx`
        );
      } catch (error) {
        this.errorMessage = "L’export Excel n’a pas pu être généré.";
      } finally {
        this.exporting = false;
      }
    },
    prepareExcelWorksheet(XLSX, worksheet) {
      if (!worksheet["!ref"]) return;
      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      const exportLabels = { ...COMMON_EXPORT_LABELS };
      this.config.columns.forEach((column) => {
        exportLabels[column.source || column.value] = column.text;
      });

      for (
        let columnIndex = range.s.c;
        columnIndex <= range.e.c;
        columnIndex += 1
      ) {
        const headerAddress = XLSX.utils.encode_cell({
          r: range.s.r,
          c: columnIndex,
        });
        const headerCell = worksheet[headerAddress];
        if (!headerCell) continue;
        const fieldName = String(headerCell.v);
        headerCell.v = exportLabels[fieldName] || fieldName;

        if (!EXCEL_NUMERIC_FIELDS.has(fieldName)) continue;
        for (
          let rowIndex = range.s.r + 1;
          rowIndex <= range.e.r;
          rowIndex += 1
        ) {
          const address = XLSX.utils.encode_cell({
            r: rowIndex,
            c: columnIndex,
          });
          const cell = worksheet[address];
          if (!cell || cell.v === "") continue;
          const number = Number(cell.v);
          if (!Number.isFinite(number)) continue;
          cell.v = number;
          cell.t = "n";
        }
      }

      worksheet["!cols"] = Array.from(
        { length: range.e.c - range.s.c + 1 },
        () => ({ wch: 18 })
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.social-reporting-table__basis {
  margin-top: 4px;
  font-size: $rmr-fs-md;
  color: $rmr-text-muted;
  max-width: 80ch;
}
</style>
